import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGetClientsQuery } from "src/app/apis/appApi"
import type { Client } from "src/db/Client"

const columns: GridColDef<Client>[] = [
  { field: "id", headerName: "ID", width: 90 },
  { field: "firstName", headerName: "First Name", width: 130 },
  { field: "lastName", headerName: "Last Name", width: 130 },
  { field: "email", headerName: "Email", width: 180 },
  { field: "organisation", headerName: "Organisation", width: 160 },
  { field: "archived", headerName: "Archived", width: 100, type: "boolean" },
  { field: "language", headerName: "Language", width: 100 },
  { field: "dateCreated", headerName: "Created", width: 140 },
  { field: "lastUpdated", headerName: "Last Updated", width: 140 },
  { field: "notes", headerName: "Notes", width: 180 },
  { field: "lastCompletedOrderDate", headerName: "Last Order", width: 140 },
]

const ClientsList: React.FC = () => {
  const { t } = useTranslation()
  const { data, error, isLoading, refetch, isFetching } = useGetClientsQuery()
  const clients: Client[] = Array.isArray(data) ? data : data?.data || []
  const [lastFetched, setLastFetched] = useState<Date | null>(null)
  const isFirstLoad = useRef(true)

  React.useEffect(() => {
    if (!isLoading && !isFetching) {
      setLastFetched(new Date())
    }
    if (isFirstLoad.current && !isLoading) {
      setLastFetched(new Date())
      isFirstLoad.current = false
    }
  }, [isLoading, isFetching])

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            {t("customers", "Clients")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("totalClients")}: {clients.length}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Tooltip title={t("refresh")}>
            <span>
              <IconButton
                onClick={() => refetch()}
                disabled={isFetching}
                size="small"
                color="primary"
              >
                {isFetching ? (
                  <CircularProgress size={24} thickness={5} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {lastFetched
              ? `${t("lastFetchedAt")} ${lastFetched.toLocaleTimeString()}`
              : ""}
          </Typography>
        </Box>
      </Box>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={120}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{t("errorLoadingClients")}</Alert>
      ) : (
        <Box sx={{ height: 520, width: "100%" }}>
          <DataGrid
            rows={clients}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
          />
        </Box>
      )}
    </Paper>
  )
}

export default ClientsList
